(() => {
  /* global Swiper */
  const CAROUSEL_NAME = "doorways-carousel";
  const SLIDE_CLASS = "swiper-slide";

  class DoorwaysCarousel extends HTMLElement {
    #swiper = null;
    #initAttempts = 0;
    #initTimer = null;
    #resizeObserver = null;

    connectedCallback() {
      this.#initAttempts = 0;
      this.#init();
    }

    disconnectedCallback() {
      if (this.#initTimer !== null) {
        window.clearTimeout(this.#initTimer);
        this.#initTimer = null;
      }

      this.#resizeObserver?.disconnect();
      this.#resizeObserver = null;

      this.#swiper?.destroy(true, true);
      this.#swiper = null;
      this.#initAttempts = 0;
    }

    #init() {
      if (this.#swiper || !this.isConnected) return;

      if (typeof Swiper === "undefined") {
        if (this.#initAttempts > 40) return;

        this.#initAttempts += 1;
        this.#initTimer = window.setTimeout(() => {
          this.#initTimer = null;
          this.#init();
        }, 50);
        return;
      }

      const swiperElement = this.querySelector(".doorways__swiper");
      if (!swiperElement) return;

      this.#prepareSlides(swiperElement);
      this.#syncSlideWidths(swiperElement);

      const slides = swiperElement.querySelectorAll(
        ".swiper-wrapper > .swiper-slide",
      );
      if (slides.length === 0) return;

      const controlsElement = this.querySelector(".doorways__controls");
      const paginationElement = this.querySelector(".doorways__pagination");
      const prevButton = this.querySelector(".doorways__nav-button--prev");
      const nextButton = this.querySelector(".doorways__nav-button--next");
      const spaceBetween = Number(this.dataset.gap ?? 12);
      const showPagination = this.dataset.pagination === "true";
      const showArrows = this.dataset.arrows === "true";
      const slideLabelTemplate = this.dataset.slideLabelTemplate ?? "";

      this.#swiper = new Swiper(swiperElement, {
        slidesPerView: "auto",
        spaceBetween,
        centerInsufficientSlides: false,
        grabCursor: true,
        watchOverflow: true,
        roundLengths: true,
        resistanceRatio: 0.85,
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
        pagination:
          showPagination && paginationElement
            ? {
                el: paginationElement,
                clickable: true,
                renderBullet: (index, className) => {
                  const label = slideLabelTemplate.replace(
                    "__SLIDE__",
                    String(index + 1),
                  );

                  return `<button type="button" class="${className}" aria-label="${label.replaceAll('"', "&quot;")}"></button>`;
                },
              }
            : false,
        navigation:
          showArrows && prevButton && nextButton
            ? {
                prevEl: prevButton,
                nextEl: nextButton,
              }
            : false,
        on: {
          lock: () => this.#syncControlsLock(controlsElement, true),
          unlock: () => this.#syncControlsLock(controlsElement, false),
        },
      });

      /* Swiper caches slide sizes at init, so the widths are written again and
         the instance updated before the lock state is read. */
      this.#syncSlideWidths(swiperElement);
      this.#swiper.update();
      this.#syncControlsLock(controlsElement, this.#swiper.isLocked);

      this.#resizeObserver = new ResizeObserver(() => {
        this.#syncSlideWidths(swiperElement);
        this.#swiper?.update();
        this.#syncControlsLock(controlsElement, this.#swiper?.isLocked);
      });
      this.#resizeObserver.observe(swiperElement);
    }

    #syncControlsLock(controlsElement, isLocked) {
      if (!controlsElement) return;

      controlsElement.classList.toggle(
        "doorways__controls--locked",
        Boolean(isLocked),
      );
    }

    #prepareSlides(swiperElement) {
      const wrapper = swiperElement.querySelector(".swiper-wrapper");
      if (!wrapper) return;

      for (const block of wrapper.querySelectorAll(":scope > .shopify-block")) {
        block.classList.add(SLIDE_CLASS);

        const tile = block.querySelector(".mini-doorway");
        if (tile) {
          tile.classList.remove(SLIDE_CLASS);
        }
      }

      for (const slide of wrapper.querySelectorAll(
        ":scope > .product-details-card-slide",
      )) {
        slide.classList.add(SLIDE_CLASS);
      }
    }

    /*
     * The card size is a floor, not a fixed width. Too few cards to fill the
     * row and they share it evenly; a row that overspills by less than one
     * card squeezes to fit rather than turning into a carousel for a sliver of
     * travel — padding on the cards puts a track over the edge often enough
     * that this is the common case. Only a genuine overflow falls back to the
     * card size, and that is what leaves Swiper the overflow it needs to
     * unlock the arrows and pagination.
     */
    #syncSlideWidths(swiperElement) {
      const slides = [
        ...swiperElement.querySelectorAll(".swiper-wrapper > .swiper-slide"),
      ];
      const count = slides.length;
      if (count === 0) return;

      const root = this.closest(".doorways") ?? this;
      root.style.setProperty("--doorway-slide-count", String(count));

      const styles = getComputedStyle(swiperElement);
      const paddingStart = Math.ceil(
        Number.parseFloat(styles.paddingInlineStart) || 0,
      );
      const paddingEnd = Math.ceil(
        Number.parseFloat(styles.paddingInlineEnd) || 0,
      );
      const available = Math.max(
        0,
        Math.floor(swiperElement.clientWidth - paddingStart - paddingEnd),
      );
      const gap = Number(this.dataset.gap ?? 12);
      const gaps = gap * Math.max(0, count - 1);

      const customWidths = new Map();
      let customTotal = 0;
      let fillCount = 0;

      for (const slide of slides) {
        const customWidth = this.#readCustomSlideWidth(slide, available);

        if (customWidth !== null) {
          customWidths.set(slide, customWidth);
          customTotal += customWidth;
        } else {
          fillCount += 1;
        }
      }

      const fillAvailable = Math.max(0, available - customTotal - gaps);
      let fillWidth = 0;

      if (fillCount > 0) {
        const fillSample =
          slides.find((slide) => !customWidths.has(slide)) ?? slides[0];
        const minWidth = this.#readMinSlideWidth(fillSample);
        const fitWidth = Math.floor(fillAvailable / fillCount);
        const totalAtMin = minWidth * fillCount;
        const overflowAtMin = totalAtMin - fillAvailable;

        if (fitWidth >= minWidth) {
          fillWidth = fitWidth;
        } else if (overflowAtMin > 0 && overflowAtMin < minWidth) {
          fillWidth = Math.max(0, fitWidth);
        } else {
          fillWidth = minWidth;
        }
      }

      for (const slide of slides) {
        const customWidth = customWidths.get(slide);
        slide.style.width = `${customWidth ?? fillWidth}px`;
      }
    }

    #readCustomSlideWidth(slide, available) {
      const tile = slide.querySelector(".mini-doorway--width-custom");
      if (!tile) return null;

      const percent = Number.parseFloat(
        getComputedStyle(tile).getPropertyValue(
          "--mini-doorway-custom-percent",
        ),
      );

      if (!Number.isFinite(percent) || percent <= 0) return null;

      return Math.max(0, Math.floor((available * percent) / 100));
    }

    /* Measured with the inline width cleared rather than read from a token, so
       card size, breakpoint and container width are all accounted for without
       mapping every card size back to a number here. */
    #readMinSlideWidth(slide) {
      const inlineWidth = slide.style.width;
      slide.style.removeProperty("width");

      const measured = slide.getBoundingClientRect().width;

      if (inlineWidth) {
        slide.style.width = inlineWidth;
      }

      return measured;
    }
  }

  if (!customElements.get(CAROUSEL_NAME)) {
    customElements.define(CAROUSEL_NAME, DoorwaysCarousel);
  }
})();
